import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import '~/styles/marketing/pricing-calculator.css';

type FurnishingState = 'furnished';
type ServiceType = 'one_time' | 'deep_clean' | 'recurring_maintenance';
type Recurrence = 'weekly' | 'biweekly' | 'every_4_weeks';

export interface PricingCalculatorWidgetProps {
  initialSqft?: number;
  initialFurnishing?: FurnishingState;
  initialService?: ServiceType;
  title?: string;
  subtitle?: string;
}

interface PricingApiResult {
  basePrice: number;
  upgradeAmount: number;
  totalPrice: number;
  estimatedHours: number;
  rateKey: string;
  rateUsed: number;
}

interface RadioOption<T extends string> {
  value: T;
  label: string;
}

const DEBOUNCE_MS = 500;
const SQFT_MIN = 200;
const SQFT_MAX = 15000;
const SQFT_STEP = 50;

const FURNISHING_OPTS: RadioOption<FurnishingState>[] = [
  { value: 'furnished', label: 'Furnished' },
];
const SERVICE_OPTS: RadioOption<ServiceType>[] = [
  { value: 'one_time', label: 'One-time clean' },
  { value: 'deep_clean', label: 'Deep clean' },
  { value: 'recurring_maintenance', label: 'Recurring' },
];
const RECURRENCE_OPTS: RadioOption<Recurrence>[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'every_4_weeks', label: 'Every 4 weeks' },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function rateLabel(r: PricingApiResult): string {
  const per = `$${r.rateUsed.toFixed(2)}/sqft`;
  const friendly = r.rateKey.replace(/^pricing_/, '').replace(/_per_sqft$/, '').replace(/_/g, ' ');
  return `${per} · ${friendly}`;
}

interface RadioPillGroupProps<T extends string> {
  name: string;
  ariaLabel: string;
  options: RadioOption<T>[];
  value: T;
  onChange: (v: T) => void;
}

function RadioPillGroup<T extends string>({ name, ariaLabel, options, value, onChange }: RadioPillGroupProps<T>) {
  return (
    <div className="pc-widget__radio-group" role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => (
        <div key={opt.value} className="pc-widget__radio-item">
          <input
            className="pc-widget__radio"
            type="radio"
            name={name}
            id={`${name}-${opt.value}`}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />
          <label className="pc-widget__radio-label" htmlFor={`${name}-${opt.value}`}>{opt.label}</label>
        </div>
      ))}
    </div>
  );
}

function ResultPanel({ loading, error, result }: { loading: boolean; error: string | null; result: PricingApiResult | null }) {
  if (loading && !result) return <p className="pc-widget__result-loading">Calculating…</p>;
  if (error) return <p className="pc-widget__result-error">{error}</p>;
  if (!result) return <p className="pc-widget__result-loading">Adjust the options to see your price.</p>;
  return (
    <>
      <p className="pc-widget__result-price">{formatCents(result.totalPrice)}</p>
      <p className="pc-widget__result-rate">{rateLabel(result)}</p>
      <dl className="pc-widget__result-breakdown">
        <dt>Base price</dt>
        <dd>{formatCents(result.basePrice)}</dd>
        {result.upgradeAmount > 0 && (
          <>
            <dt>Deep clean upgrade</dt>
            <dd>{formatCents(result.upgradeAmount)}</dd>
          </>
        )}
        <dt>Estimated time</dt>
        <dd>{result.estimatedHours.toFixed(1)} hrs</dd>
      </dl>
    </>
  );
}

export function PricingCalculatorWidget({
  initialSqft = 1500,
  initialFurnishing = 'furnished',
  initialService = 'one_time',
  title = 'Instant Pricing Estimate',
  subtitle = 'Move the slider and pick your service. We pull live rates — no estimates that change later.',
}: PricingCalculatorWidgetProps) {
  const navigate = useNavigate();
  const [sqft, setSqft] = useState<number>(initialSqft);
  const [furnishing, setFurnishing] = useState<FurnishingState>(initialFurnishing);
  const [serviceType, setServiceType] = useState<ServiceType>(initialService);
  const [recurrence, setRecurrence] = useState<Recurrence>('biweekly');
  const [includeDeepUpgrade, setIncludeDeepUpgrade] = useState<boolean>(false);
  const [result, setResult] = useState<PricingApiResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const upgradeEligible = furnishing === 'furnished' && serviceType === 'recurring_maintenance';

  const payload = useMemo(
    () => ({
      sqft,
      furnishingState: furnishing,
      serviceType,
      ...(serviceType === 'recurring_maintenance' ? { recurrenceFrequency: recurrence } : {}),
      ...(upgradeEligible && includeDeepUpgrade ? { includeDeepUpgrade: true } : {}),
    }),
    [sqft, furnishing, serviceType, recurrence, includeDeepUpgrade, upgradeEligible],
  );

  useEffect(() => {
    if (sqft < SQFT_MIN || sqft > SQFT_MAX) return;
    const ctrl = new AbortController();
    const timer = window.setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/pricing/calculate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: ctrl.signal,
        });
        const data = (await res.json()) as { ok: boolean; data?: PricingApiResult; error?: string };
        if (!res.ok || !data.ok || !data.data) {
          setError(data.error ?? 'Could not calculate price.');
          setResult(null);
        } else {
          setResult(data.data);
        }
      } catch (err) {
        if ((err as Error).name !== 'AbortError') {
          setError('Network error. Please try again.');
          setResult(null);
        }
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [payload, sqft]);

  function handleSqftChange(v: string) {
    const parsed = parseInt(v, 10);
    if (Number.isNaN(parsed)) return;
    setSqft(Math.max(SQFT_MIN, Math.min(SQFT_MAX, parsed)));
  }

  function handleCta() {
    const params = new URLSearchParams({ sqft: String(sqft), furnishing, service: serviceType });
    if (serviceType === 'recurring_maintenance') params.set('frequency', recurrence);
    if (upgradeEligible && includeDeepUpgrade) params.set('upgrade', '1');
    navigate(`/get-a-quote?${params.toString()}`);
  }

  return (
    <div className="pc-widget">
      <div className="pc-widget__header">
        <h2 className="pc-widget__title">{title}</h2>
        <p className="pc-widget__subtitle">{subtitle}</p>
      </div>
      <div className="pc-widget__section">
        <label className="pc-widget__label" htmlFor="pc-sqft">Home size</label>
        <div className="pc-widget__sqft-row">
          <input className="pc-widget__slider" type="range" min={SQFT_MIN} max={SQFT_MAX} step={SQFT_STEP} value={sqft} onChange={(e) => handleSqftChange(e.target.value)} aria-label="Square footage slider" />
          <input id="pc-sqft" className="pc-widget__sqft-input" type="number" min={SQFT_MIN} max={SQFT_MAX} step={SQFT_STEP} value={sqft} onChange={(e) => handleSqftChange(e.target.value)} />
          <span className="pc-widget__sqft-suffix">sq ft</span>
        </div>
      </div>
      <div className="pc-widget__section">
        <span className="pc-widget__label">Furnishing</span>
        <RadioPillGroup name="pc-furnishing" ariaLabel="Furnishing state" options={FURNISHING_OPTS} value={furnishing} onChange={setFurnishing} />
      </div>
      <div className="pc-widget__section">
        <span className="pc-widget__label">Service type</span>
        <RadioPillGroup name="pc-service" ariaLabel="Service type" options={SERVICE_OPTS} value={serviceType} onChange={setServiceType} />
      </div>
      {serviceType === 'recurring_maintenance' && (
        <div className="pc-widget__section">
          <span className="pc-widget__label">Frequency</span>
          <RadioPillGroup name="pc-recurrence" ariaLabel="Recurrence frequency" options={RECURRENCE_OPTS} value={recurrence} onChange={setRecurrence} />
        </div>
      )}
      {upgradeEligible && (
        <div className="pc-widget__section">
          <label className="pc-widget__checkbox-row">
            <input className="pc-widget__checkbox" type="checkbox" checked={includeDeepUpgrade} onChange={(e) => setIncludeDeepUpgrade(e.target.checked)} />
            <span className="pc-widget__checkbox-text">
              <strong>Add deep clean upgrade</strong> — bring this visit up to full deep-clean scope.
            </span>
          </label>
        </div>
      )}
      <div className="pc-widget__result" aria-live="polite">
        <p className="pc-widget__result-label">Your estimate</p>
        <ResultPanel loading={loading} error={error} result={result} />
      </div>
      <button type="button" className="pc-widget__cta" onClick={handleCta}>
        Get My Quote &rarr;
      </button>
    </div>
  );
}
